import { Subscription } from "rxjs";
import { OnDestroy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ModalName } from "src/app/enums/modal-name";
import { IClientNumber } from "src/app/models/client/client-number";
import { IContact } from "src/app/models/contact/contact";
import { ICoverPage } from "src/app/models/cover-page/cover-page";
import { IGenerateFaxPdfPayload } from "src/app/models/fax/generate-fax-pdf-payload";
import { ClientService } from "src/app/services/client.service";
import { CoverPageService } from "src/app/services/cover-page.service";
import { ModalService } from "src/app/services/modal.service";

@Component({
  selector: "app-fax-recipient-info-form",
  templateUrl: "./fax-recipient-info-form.component.html",
  styleUrls: ["./fax-recipient-info-form.component.scss"],
})
export class FaxRecipientInfoFormComponent implements OnDestroy, OnInit {
  private _numbersSubscription?: Subscription;
  @Input() defaultData: IGenerateFaxPdfPayload | null = null;

  @Output() submit = new EventEmitter<IGenerateFaxPdfPayload>();

  clientNumbers: IClientNumber[] = [];
  coverPages: ICoverPage[] = [];

  form = new UntypedFormGroup({
    toNumberCode: new UntypedFormControl("+1", [
      Validators.required,
      Validators.pattern("[+]{1}[0-9]{1,3}"),
    ]),
    toNumber: new UntypedFormControl("", [Validators.required, Validators.pattern("[0-9]{10}")]),
    deliveryTo: new UntypedFormControl("", [Validators.required, Validators.pattern(/.*\S.*/)]),
    faxSubject: new UntypedFormControl("", [Validators.required, Validators.pattern(/.*\S.*/)]),
    fromNumber: new UntypedFormControl("", Validators.required),
    sentFrom: new UntypedFormControl("", [Validators.required, Validators.pattern(/.*\S.*/)]),
    coverPage: new UntypedFormControl("none"),
    generateCoverPage: new UntypedFormControl(false, Validators.required),
    notes: new UntypedFormControl("", Validators.maxLength(120)),
  });

  constructor(
    private readonly _clientService: ClientService,
    private readonly _coverPagesService: CoverPageService,
    private readonly _modalService: ModalService
  ) {}

  get notesLength() {
    return this.form.get("notes")?.value?.length || 0;
  }

  leaveNumbersAndPlusChars(event: KeyboardEvent) {
    return isNaN(parseInt(event.key)) === false || event.key === "+";
  }

  leaveNumbersChars(event: KeyboardEvent) {
    return isNaN(parseInt(event.key)) === false;
  }

  openSelectContactModal() {
    this._modalService.openModal(ModalName.SelectContact);
  }

  clearNumberAndDeliverToFields() {
    this.form.get("deliveryTo")?.setValue?.("");
    this.form.get("toNumber")?.setValue?.("");
    this.form.get("toNumberCode")?.setValue?.("+1");
  }

  openCreateCoverPageModal() {
    this._modalService.openModal(ModalName.CreateCoverPage);
  }

  createCoverPage(payload: ICoverPage) {
    this._coverPagesService.create(payload).subscribe(() => {
      this.getCoverPages(payload.title);
    });
  }

  private selectCoverPage(coverPageTitle: string) {
    if (coverPageTitle === "add") {
      this.form.get("coverPage")?.setValue("none", { emitEvent: false });
      this.openCreateCoverPageModal();

      return;
    }

    if (coverPageTitle === "none") {
      this.form.get("sentFrom")?.setValue?.("");
      this.form.get("generateCoverPage")?.setValue?.(false);

      return;
    }

    const selectedCoverPage = this.coverPages.find(cp => cp.title === coverPageTitle);

    this.form.get("sentFrom")?.setValue?.(selectedCoverPage?.sentFrom ?? "");
    if (selectedCoverPage) {
      this.form.get("fromNumber")?.setValue(selectedCoverPage.fromNumber);
    }
    this.form.get("generateCoverPage")?.setValue?.(!!selectedCoverPage);
  }

  onSubmit() {
    if (this.form.invalid) {
      return;
    }

    const form = this.form.value;

    const selectedClientNumber = this.clientNumbers.find(cn => cn.number === form?.fromNumber);

    const faxPdfPayload: IGenerateFaxPdfPayload = {
      toNumber: `${form?.toNumberCode}${form?.toNumber}`,
      fromNumber: selectedClientNumber?.number ?? "",
      generateCoverPage: !!form?.generateCoverPage ||
        [form?.sentFrom, form?.deliveryTo, form?.faxSubject, form?.notes].every(
          value => String(value ?? "").trim().length > 0
        ),
      files: [],
    };

    if (form?.sentFrom?.length > 0) {
      faxPdfPayload.sentFrom = form.sentFrom;
    }

    if (form?.faxSubject?.length > 0) {
      faxPdfPayload.faxSubject = form.faxSubject;
    }

    if (form?.deliveryTo?.length > 0) {
      faxPdfPayload.deliveryTo = form.deliveryTo;
    }

    if (form?.notes?.length >= 2) {
      faxPdfPayload.notes = form.notes.replaceAll("\n", "<br>");
    }

    this.submit.emit(faxPdfPayload);
  }

  selectContact(contact: IContact) {
    let contactFaxNumber = contact.faxNumber;

    if (contact.faxNumber.includes("+")) {
      this.form.get("toNumberCode")?.setValue?.(contact.faxNumber.slice(0, 2));
      contactFaxNumber = contact.faxNumber.slice(2);
    }

    this.form.get("toNumber")?.setValue?.(contactFaxNumber);
    this.form.get("deliveryTo")?.setValue?.(contact.name);
  }

  ngOnInit() {
    this.patchFormWithDefaultData();

    this.getClientNumbers();
    this.setFromNumberAsPrimaryClientNumber();
    this.getCoverPages();

    this.form.get("coverPage")?.valueChanges.subscribe(value => this.selectCoverPage(value));
  }

  private patchFormWithDefaultData() {
    if (!this.defaultData) {
      return;
    }

    let toNumberCode = "+1";
    let toNumber = this.defaultData.toNumber ?? "";

    if (toNumber.includes("+")) {
      toNumberCode = toNumber.slice(0, 2);
      toNumber = toNumber.slice(2);
    }

    this.form.patchValue({
      toNumberCode,
      toNumber,
      deliveryTo: this.defaultData.deliveryTo ?? "",
      faxSubject: this.defaultData.faxSubject ?? "",
      fromNumber: this.defaultData.fromNumber ?? "",
      sentFrom: this.defaultData.sentFrom ?? "",
      generateCoverPage: this.defaultData.generateCoverPage ?? false,
      notes: this.defaultData.notes ?? "",
    });
  }

  private getClientNumbers() {
    this._numbersSubscription = this._clientService.watchNumbers().subscribe(clientNumbers => {
      this.clientNumbers = clientNumbers;

      if (clientNumbers.length == 0) {
        return;
      }

      const from = this.form.get("fromNumber")?.value;
      if (from == null || from.length == 0) {
        this.form.get("fromNumber")?.setValue?.(clientNumbers[0].number);
      }
    });
  }

  private setFromNumberAsPrimaryClientNumber() {
    const primaryClientNumber = this.clientNumbers.find(cn => cn.isPrimary);
    if (primaryClientNumber) {
      this.form.get("fromNumber")?.setValue?.(primaryClientNumber?.number);
    }
  }

  private getCoverPages(selectTitle?: string) {
    this._coverPagesService.getAll({ pageNumber: "1", pageSize: "10" }).subscribe(coverPages => {
      this.coverPages = coverPages.records;

      if (selectTitle) {
        this.form.get("coverPage")?.setValue(selectTitle);
      }
    });
  }

  ngOnDestroy() {
    this._numbersSubscription?.unsubscribe();
  }
}
